/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Input,
  TextArea,
  CheckboxButtonGroup,
  CheckboxToggle,
  Button,
  SvgIcon
} from '../'

import {
  readablePhoneNumber,
  notifyCoreID
} from '../../utils'

import { COREID_ACTIVE } from '../../constants'


class AddressEdit extends React.PureComponent {
  static propTypes = {
    identity: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    addressId: PropTypes.string.isRequired,
    addressType: PropTypes.oneOf([ 'MSISDN', 'LANDLINE', 'EMAIL', 'PHYSICAL' ]),
    addressValue: PropTypes.string.isRequired,
    default: PropTypes.bool.isRequired,
    optedOut: PropTypes.bool.isRequired,
    save: PropTypes.bool,
    onSaved: PropTypes.func.isRequired,
    onFailed: PropTypes.func.isRequired
  }

  state = {
    addressValue: this.props.addressValue,
    optedOut: this.props.optedOut,
    default: this.props.default,
    inactive: false,
    showOptInConfirmModal: false,
    showAddressArchived: false
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.save &&
      (
        this.state.addressValue !== this.props.addressValue ||
        this.state.default !== this.props.default ||
        this.state.optedOut !== this.props.optedOut
      )) {
      this.saveAddress()
    }
  }

  saveAddress = (archived = false) => {
    let input = {
      id: this.props.addressId
    }

    let CoreIDFields = {}

    if (this.state.addressValue !== this.props.addressValue) {
      input.addressValue = this.state.addressValue
      if (COREID_ACTIVE) CoreIDFields["address_value"] = this.state.addressValue
    }
    if (this.state.default !== this.props.default) {
      input.default = this.state.default
    }
    if (this.state.optedOut !== this.props.optedOut) {
      input.optedOut = this.state.optedOut
    }
    if (archived) {
      input.inactive = true
    }

    this.props.saveAddress({ variables: { input } })
      .then(({ data }) => {
        console.log('Address saved successfully', data)
        if (archived) {
          // passing 'true' to onSaved to show archived notification
          this.props.onSaved(true)
        } else {
          this.props.onSaved()
        }
        if (COREID_ACTIVE && Object.keys(CoreIDFields).length > 0) {
          notifyCoreID(CoreIDFields, data.addressMutate.address.identity.identityValue)
        }
      })
      .catch((error) => {
        console.log('Saving address failed.', error)
        this.props.onFailed()
      })
  }

  showArchiveButton = (label) => (
    <Button
      className='slds-col_bump-left'
      maxHeight={35}
      minWidth={100}
      onClick={() => this.saveAddress(true)}>
      <SvgIcon
        variant='utility'
        symbol='delete'
        size='x-small'
        className='slds-m-right_x-small'
      />
      {label}
    </Button>
  )

  render() {
    let { addressType } = this.props

    if (['MSISDN', 'LANDLINE', 'EMAIL'].indexOf(addressType) > -1) {
      return (
        <React.Fragment>
          {this.state.showOptInConfirmModal &&
            <OptInConfirmModal
              firstName={this.props.firstName}
              onNoClick={() => this.setState({ optedOut: true, showOptInConfirmModal: false })}
              onYesClick={() => this.setState({ optedOut: false, showOptInConfirmModal: false })}
            />
          }
          <EditBanner />
          <div className='slds-border_bottom slds-p-around_medium slds-grid'>
            <Input
              placeholder={`Enter ${addressType === 'EMAIL' ? 'address' : 'number'}`}
              value={['MSISDN', 'LANDLINE'].indexOf(addressType) > -1
                ? readablePhoneNumber(this.state.addressValue)
                : this.state.addressValue
              }
              onChange={(e) => this.setState({ addressValue: e.target.value })}
              className='slds-size_1-of-4'
            />
            {this.showArchiveButton('Archive number')}
          </div>
          <div className='slds-size_1-of-1 slds-grid slds-border_bottom slds-p-around_medium'>
            {{
              'MSISDN': 'Primary cellphone number',
              'LANDLINE': 'Primary landline number',
              'EMAIL': 'Primary email address'
            }[addressType]}
            <CheckboxToggle
              id={`primary_${this.props.addressId}`}
              checked={this.state.default}
              onChange={() => this.setState(prevState => (
                { default: !prevState.default }
              ))}
              className='slds-col_bump-left'
            />
          </div>
          <div className='slds-size_1-of-1 slds-grid slds-border_bottom slds-p-around_medium'>
            Communication consent
            <CheckboxButtonGroup
              id={`consent_${this.props.addressId}`}
              options={[
                'Opted IN',
                'Opted OUT',
                'None'
              ]}
              selectedOption={this.state.optedOut === null
                ? 'None'
                : this.state.optedOut ? 'Opted OUT' : 'Opted IN'
              }
              onChange={(e) => {
                let value = e.target.value
                if (value === 'Opted IN') {
                  this.setState({ showOptInConfirmModal: true })
                } else if (value === 'Opted OUT') {
                  this.setState({ optedOut: true })
                } else {
                  this.setState({ optedOut: null })
                }
              }}
              className='slds-col_bump-left'
            />
          </div>
        </React.Fragment>
      )
    } else {
      return (
        <React.Fragment>
          <EditBanner />
          <div className='slds-grid slds-p-around_medium'>
            <TextArea
              value={this.state.addressValue.replace(/ *, */g, '\n')} // replace commas, that might include white space(s) either side, with newline
              onChange={(e) => this.setState({ addressValue: e.target.value })}
              className='slds-size_1-of-2'
              rows={5}
            />
            {this.showArchiveButton('Archive address')}
          </div>
        </React.Fragment>
      )
    }
  }
}

const saveAddress = gql`
  mutation saveAddress($input: AddressMutationInput!) {
    addressMutate(input: $input) {
      address {
        id
        identity {
          id
          identityValue
        }
        addressType
        addressValue
        default
        inactive
        optedOut
      }
    }
  }
`

const withAddressMutate =
  graphql(saveAddress, {
    name: 'saveAddress'
  })

export default withAddressMutate(AddressEdit)

class OptInConfirmModal extends React.PureComponent {
  static propTypes = {
    firstName: PropTypes.string.isRequired,
    onNoClick: PropTypes.func.isRequired,
    onYesClick: PropTypes.func.isRequired
  }

  render() {
    const modalsEl = document.getElementById('modals')

    return (
      ReactDOM.createPortal(
        <div>
          <section
            role='dialog'
            tabIndex='-1'
            className='slds-modal slds-fade-in-open'>
            <div className='slds-modal__container'>
              <div className='slds-modal__content slds-p-around_x-large' style={{ backgroundColor: 'green', opacity: 0.65 }}>
                <div className='slds-p-bottom_medium slds-text-color_inverse slds-text-align_center'>
                  {`${this.props.firstName} has previously opted out of communications
                   for this number. Do you have their consent?`}
                </div>
                <div className='slds-align_absolute-center'>
                  <Button
                    type='inverse'
                    onClick={this.props.onNoClick}
                    className='slds-m-right_small'>
                    No
                  </Button>
                  <Button
                    type='inverse'
                    onClick={this.props.onYesClick}>
                    Yes
                  </Button>
                </div>
              </div>
            </div>
          </section>
          <div className="slds-backdrop slds-backdrop_open"></div>
        </div>,
        modalsEl
      )
    )
  }
}

const EditBanner = (props) => (
  <div className='slds-text-body_small u-font-weight-bold'>
    Only use this edit form to correct mistakes. Do not change a person's contact
    details, rather add a new entry.
  </div>
)



// WEBPACK FOOTER //
// ./src/components/address-edit/index.js